import type { DocId, MemberRolesMap, Timestamp, UserId } from './common.js';

/**
 * Number of color slots in the deterministic palette. Both web and
 * functions consult this constant when assigning a color index to a new
 * assignee — keeping the modulus pinned here means a new assignee gets
 * the same slot regardless of which side created it.
 *
 * Mirrors the palette length in `web/src/lib/assigneeColor.ts`. If the
 * palette grows or shrinks, bump this together with the array.
 */
/**
 * 2026-04-29: Trimmed from 17 → 10 distinct hues + a gray slot.
 * Existing assignees with `colorIndex >= 10` will wrap via modulus
 * (`12 -> 2`) the next time their chip renders; that's an accepted
 * one-time visual shift in exchange for a palette where neighbouring
 * slots are easy to tell apart.
 */
export const ASSIGNEE_COLOR_SLOTS = 10;

/**
 * Deterministic FNV-1a-style hash of an assignee's display name. Kept in
 * shared so the **same name resolves to the same color** in WBS, the
 * migration-task list, the DAG canvas, and the settings page — and so
 * the backfill script (functions) and the create-assignee UI (web) agree
 * on the color slot when neither side has a stored `colorIndex` yet.
 *
 * Note: this duplicates the historical hash in `web/src/lib/assigneeColor.ts`
 * intentionally so legacy WBS rows whose only "assignee identity" is a
 * free-text name continue to render the same color after the migration.
 */
export function assigneeColorIndex(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = ((h * 31) + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % ASSIGNEE_COLOR_SLOTS;
}

/**
 * `/projects/{projectId}/assignees/{assigneeId}` — single entry in the
 * project's assignee list.
 *
 * An *assignee* is a label-with-color identity used throughout the
 * project — distinct from a Firebase Auth user. Assignees may eventually
 * be linked to a real user via `/projects/{pid}/assigneeUserMap` (see
 * `AssigneeUserMappingDoc`), but that mapping is optional: outsourced
 * vendors and yet-to-be-onboarded staff exist as assignees with no
 * linked user.
 *
 * Migration-task documents (`TaskDoc.assigneeId`), WBS rows
 * (`WbsItemDoc.assigneeId`), and any future executor-of-work surface
 * should reference these IDs instead of duplicating the display name.
 *
 * Lifecycle:
 *   - Created from the WBS UI ("担当者リスト" section in Settings) or
 *     auto-created by the import / backfill scripts when an Excel sheet
 *     names someone the project has never heard of.
 *   - `colorIndex` is captured at creation time so renames don't change
 *     the color. When absent (legacy/imported rows that only carry a
 *     name), call sites fall back to `assigneeColorIndex(name)`.
 */
export interface AssigneeDoc {
  assigneeId: DocId;
  projectId: string;

  /** Display name (e.g. "佐藤 太郎", "横河SS（外注）"). */
  name: string;

  /**
   * 0..(ASSIGNEE_COLOR_SLOTS-1) — index into the shared palette. Frozen
   * at creation time so renaming an assignee never reshuffles their
   * color across views.
   */
  colorIndex: number;

  /**
   * `'manual'` — a human added this entry from the WBS / Settings UI.
   * `'import'` — auto-created by an Excel-import path (seed or backfill)
   *              from a free-text assignee name. Kept distinct so the
   *              UI can flag "needs review" for un-curated entries.
   */
  source: 'manual' | 'import';

  /** Denormalized from parent project for Security Rules (addendum §2). */
  memberUserIds: UserId[];
  memberRoles: MemberRolesMap;

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * `/projects/{projectId}/assigneeUserMap/{assigneeId}` — links an
 * `AssigneeDoc` to a Firebase Auth user (project member) **and / or** to
 * a Backlog space user.
 *
 * Doc ID intentionally equals the `assigneeId` so the link is 1:1 from
 * the assignee side. The 1:1-from-the-user-side constraint (each user
 * may map to at most one assignee per project) is enforced by the
 * write-side helper in `web/src/lib/assigneeOps.ts` plus an active
 * lookup in Firestore Rules — see the rules file for the dual-defense
 * argument.
 *
 * Absence of a doc means "this assignee is linked to neither a real user
 * nor a Backlog user". That's the default; both links are opt-in from
 * the Settings page.
 *
 * The doc may carry **either link independently**:
 *   - a Migratus `userId` (for "自分の担当" filters / notifications), and / or
 *   - a `backlogUserId` (the authoritative key the Backlog → WBS import
 *     uses to resolve `assignee` despite display-name ゆらぎ).
 *
 * That is why `userId` is now optional: an import-sourced assignee with a
 * linked Backlog user but no onboarded Migratus member is a first-class
 * state. Firestore Rules permit a doc with `userId` absent as long as,
 * when present, it names a project member.
 */
export interface AssigneeUserMappingDoc {
  /** Same as the doc ID; lets `array-contains` queries work on `assigneeId`. */
  assigneeId: DocId;
  projectId: string;

  /**
   * Firebase Auth UID of the linked project member. Optional: a doc may
   * exist purely to carry a Backlog link (`backlogUserId`) for an
   * assignee that has no onboarded Migratus member yet. When present it
   * always names a real project member (never the empty string).
   */
  userId?: UserId;

  /**
   * Backlog's numeric user id (unique within the space). The
   * authoritative key the Backlog → WBS import resolves `assignee`
   * against — id-first matching sidesteps display-name ゆらぎ
   * (e.g. Backlog「佐野友貴子」⇔ Migratus「佐野」). `null` / absent means
   * no Backlog user is linked.
   */
  backlogUserId?: number | null;

  /**
   * Denormalized Backlog display name captured when the link was made
   * (or last refreshed from the picker). Display-only — the import never
   * matches on it. Not auto-synced when Backlog renames the user; the UI
   * offers a manual "再取得".
   */
  backlogUserName?: string | null;

  /** Denormalized from parent project for Security Rules. */
  memberUserIds: UserId[];
  memberRoles: MemberRolesMap;

  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdByUserId: UserId;
}
